import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { fmtINR, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { exportXlsx } from "@/lib/xlsx-export";
import { Download } from "lucide-react";

export default function StockAgeing() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/stock-ageing").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;
  const bt = d.bucket_totals || {};
  const cards = [
    { k: "0-30",  tone: "border-emerald-500/40 bg-emerald-500/10 text-emerald-800" },
    { k: "31-60", tone: "border-amber-500/40 bg-amber-500/10 text-amber-800" },
    { k: "61-90", tone: "border-orange-500/40 bg-orange-500/10 text-orange-800" },
    { k: "90+",   tone: "border-red-500/40 bg-red-500/10 text-red-800" },
  ];
  return (
    <div className="space-y-4" data-testid="stock-ageing">
      <div className="text-[10px] font-mono uppercase text-muted-foreground">// Stock ageing · buckets by inward date</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c) => (
          <Card key={c.k} className={`rounded-sm border p-3 ${c.tone}`}>
            <div className="text-[10px] font-mono uppercase">{c.k} days</div>
            <div className="text-2xl font-bold tabular-nums mt-1">{fmtINR(bt[c.k] || 0)}</div>
          </Card>
        ))}
      </div>

      {/* Iter-101 · Warehouse ageing split */}
      {(d.by_warehouse || []).length > 0 && (
        <div className="card-sharp overflow-hidden" data-testid="ageing-by-warehouse">
          <div className="p-2 flex items-center justify-between bg-muted/40">
            <div className="text-[10px] font-mono uppercase text-muted-foreground">// Split by warehouse</div>
            <Button size="sm" variant="outline" className="rounded-sm gap-1 h-7"
                      onClick={() => exportXlsx({
                        filename: `stock_ageing_by_warehouse_${new Date().toISOString().slice(0,10)}.xlsx`,
                        sheetName: "Ageing by Warehouse",
                        columns: [
                          { key: "warehouse_name", label: "Warehouse" },
                          { key: "0-30",  label: "0-30 ₹" },
                          { key: "31-60", label: "31-60 ₹" },
                          { key: "61-90", label: "61-90 ₹" },
                          { key: "90+",   label: "90+ ₹" },
                          { key: "total", label: "Total ₹" },
                        ],
                        rows: d.by_warehouse,
                      })}
                      data-testid="ageing-by-wh-export">
              <Download className="w-3 h-3" /> XLSX
            </Button>
          </div>
          <table className="w-full dense">
            <thead><tr><th>Warehouse</th><th className="text-right">0-30 ₹</th><th className="text-right">31-60 ₹</th><th className="text-right">61-90 ₹</th><th className="text-right">90+ ₹</th><th className="text-right">Total ₹</th></tr></thead>
            <tbody>
              {(d.by_warehouse || []).map((w) => (
                <tr key={w.warehouse_name}>
                  <td className="font-medium">{w.warehouse_name}</td>
                  <td className="text-right tabular-nums text-emerald-700">{fmtINR(w["0-30"])}</td>
                  <td className="text-right tabular-nums text-amber-700">{fmtINR(w["31-60"])}</td>
                  <td className="text-right tabular-nums text-orange-700">{fmtINR(w["61-90"])}</td>
                  <td className="text-right tabular-nums text-red-700">{fmtINR(w["90+"])}</td>
                  <td className="text-right tabular-nums font-semibold">{fmtINR(w.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Inward Date</th><th className="text-right">Age (d)</th><th>Bucket</th><th>SKU</th><th>Item</th><th>Warehouse</th><th>Batch</th><th className="text-right">Qty</th><th className="text-right">Value ₹</th><th>Source</th></tr></thead>
          <tbody>
            {(d.rows || []).length === 0 && <tr><td colSpan="10" className="text-center py-10 text-muted-foreground">No inward stock movements yet.</td></tr>}
            {(d.rows || []).map((r) => (
              <tr key={r.movement_id} data-testid={`ag-row-${r.movement_id}`}>
                <td className="text-xs">{fmtDate(r.date)}</td>
                <td className="text-right tabular-nums">{r.age_days}</td>
                <td className="text-[10px] font-mono uppercase">{r.bucket}</td>
                <td className="font-mono text-xs">{r.sku}</td>
                <td>{r.name}</td>
                <td>{r.warehouse_name}</td>
                <td className="font-mono text-xs text-muted-foreground">{r.batch_no || "—"}</td>
                <td className="text-right tabular-nums">{r.qty}</td>
                <td className="text-right tabular-nums">{fmtINR(r.value)}</td>
                <td className="text-xs text-muted-foreground truncate max-w-[10rem]">{r.source_ref}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
